import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { marketplaceAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Search, Building2, Users, CheckCircle, Send, RefreshCw, Loader2,
  UserPlus, Calendar, Award, Target, Clock, Star, TrendingUp,
  FileText, Globe, ShieldCheck, MapPin, Zap, BarChart3, CheckCircle2,
  XCircle, ExternalLink, Lock, ArrowRight
} from 'lucide-react';

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) { return dateStr; }
}

function formatDateShort(dateStr) {
  if (!dateStr) return { month: 'N/A', day: '' };
  try {
    var date = new Date(dateStr);
    var month = date.toLocaleDateString('en-US', { month: 'short' });
    var day = date.getDate();
    return { month: month, day: day };
  } catch (e) { return { month: 'N/A', day: '' }; }
}

function formatSID(sellerId) {
  if (!sellerId) return '';
  return 'SID:' + sellerId.substring(0, 8).toUpperCase();
}

function getQualityColor(qualityStr) {
  var num = parseFloat(qualityStr) || 0;
  if (num >= 80) return 'text-emerald-600';
  if (num >= 50) return 'text-amber-600';
  return 'text-slate-500';
}

function getQualityBadgeStyle(qualityStr) {
  var num = parseFloat(qualityStr) || 0;
  if (num >= 80) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (num >= 50) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
}

function BadgeList(props) {
  var items = props.items;
  var colorClass = props.colorClass;
  if (!items || items.length === 0) return null;
  var badges = [];
  for (var i = 0; i < items.length; i++) {
    badges.push(<Badge key={i} variant="secondary" className={colorClass}>{items[i]}</Badge>);
  }
  return <div className="flex flex-wrap gap-2">{badges}</div>;
}

function ComplianceBadge(props) {
  var bgClass = props.isCompliant 
    ? 'bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200' 
    : 'bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200';
  var icon = props.isCompliant 
    ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
    : <XCircle className="w-5 h-5 text-slate-300" />;
  var textClass = props.isCompliant ? 'text-emerald-700' : 'text-slate-500';
  return (
    <div className={'flex items-center gap-3 p-4 rounded-xl border ' + bgClass}>
      {icon}
      <span className={'text-sm font-semibold ' + textClass}>{props.label}</span>
    </div>
  );
}

function ProfileSection(props) {
  var Icon = props.icon;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="px-5 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <div className={'w-8 h-8 rounded-lg flex items-center justify-center ' + (props.iconBg || 'bg-slate-100')}>
            <Icon className={'w-4 h-4 ' + props.iconColor} />
          </div>
          {props.title}
        </h3>
      </div>
      <div className="p-5">
        {props.children}
      </div>
    </div>
  );
}

function SellersMarketplacePage() {
  var auth = useAuth();
  var isConnectedWith = auth.isConnectedWith;
  var isPendingWith = auth.isPendingWith;
  var addPendingRequest = auth.addPendingRequest;
  var refreshSession = auth.refreshSession;
  var user = auth.user;

  var sellerState = useState([]);
  var sellers = sellerState[0];
  var setSellers = sellerState[1];
  
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];
  
  var searchState = useState('');
  var searchText = searchState[0];
  var setSearchText = searchState[1];
  
  var reqDialogState = useState(false);
  var requestDialogOpen = reqDialogState[0];
  var setRequestDialogOpen = reqDialogState[1];
  
  var selSellerState = useState(null);
  var selectedSeller = selSellerState[0];
  var setSelectedSeller = selSellerState[1];
  
  var subjectState = useState('');
  var requestSubject = subjectState[0];
  var setRequestSubject = subjectState[1];
  
  var msgState = useState('');
  var requestMessage = msgState[0];
  var setRequestMessage = msgState[1];
  
  var sendingState = useState(false);
  var sendingRequest = sendingState[0];
  var setSendingRequest = sendingState[1];
  
  var profileDialogState = useState(false);
  var profileDialogOpen = profileDialogState[0];
  var setProfileDialogOpen = profileDialogState[1];
  
  var selProfileState = useState(null);
  var selectedProfile = selProfileState[0];
  var setSelectedProfile = selProfileState[1];
  
  var loadProfileState = useState(false);
  var loadingProfile = loadProfileState[0];
  var setLoadingProfile = loadProfileState[1];

  // Refresh session to get latest pending/connected sellers on page load
  useEffect(function() {
    if (user && user.id && user.role === 'buyer') {
      console.log('Refreshing session to get latest pending/connected sellers...');
      refreshSession(user.id, user.role);
    }
  }, []);

  useEffect(function() { fetchSellers(); }, []);

  function fetchSellers() {
    setLoading(true);
    setError(null);
    marketplaceAPI.getSellers()
      .then(function(response) {
        if (response.data.success) { setSellers(response.data.data || []); }
        else { setError(response.data.error || 'Failed to fetch sellers'); }
      })
      .catch(function() { setError('Failed to connect to marketplace'); })
      .finally(function() { setLoading(false); });
  }

  function openRequestDialog(seller) {
    setSelectedSeller(seller);
    setRequestSubject('Business Partnership Request');
    setRequestMessage('Hi, I would like to connect with you for potential lead generation collaboration.');
    setRequestDialogOpen(true);
  }

  function closeRequestDialog() {
    setRequestDialogOpen(false);
    setSelectedSeller(null);
  }

  function handleSendRequest() {
    if (!selectedSeller || !requestSubject || !requestMessage) {
      toast.error('Please fill in all fields');
      return;
    }
    setSendingRequest(true);
    marketplaceAPI.sendRequest({
      to_user_id: selectedSeller.seller_id,
      to_role: 'seller',
      subject: requestSubject,
      message: requestMessage
    })
      .then(function(response) {
        if (response.data.status === 'success' || response.data.success) {
          toast.success('Connection request sent successfully!');
          addPendingRequest(selectedSeller.seller_id);
          closeRequestDialog();
        } else {
          // Handle non-success responses (e.g., already pending)
          var message = (response.data.message || '').toLowerCase();
          if (message.includes('pending') || message.includes('already')) {
            if (message.includes('connected') || message.includes('accepted')) {
              toast.info('You are already connected with this seller');
              auth.addConnectedUser(selectedSeller.seller_id);
            } else {
              toast.info('A connection request is already pending with this seller');
              addPendingRequest(selectedSeller.seller_id);
            }
            closeRequestDialog();
          } else {
            toast.error(response.data.message || 'Failed to send request');
          }
        }
      })
      .catch(function(err) {
        var status = err.response && err.response.status;
        var detail = err.response && err.response.data && err.response.data.detail;
        var message = err.response && err.response.data && err.response.data.message;
        var errorText = (detail || message || '').toLowerCase();
        
        // Handle 409 Conflict or already pending errors
        if (status === 409 || errorText.includes('pending') || errorText.includes('already')) {
          if (errorText.includes('connected') || errorText.includes('accepted')) {
            toast.info('You are already connected with this seller');
            auth.addConnectedUser(selectedSeller.seller_id);
          } else {
            toast.info('A connection request is already pending with this seller');
            addPendingRequest(selectedSeller.seller_id);
          }
          closeRequestDialog();
        } else {
          toast.error(detail || message || 'Failed to send request');
        }
      })
      .finally(function() { setSendingRequest(false); });
  }

  function openProfileDialog(seller) {
    setSelectedProfile(null);
    setProfileDialogOpen(true);
    setLoadingProfile(true);
    
    marketplaceAPI.getSellerProfile(seller.seller_id)
      .then(function(response) {
        var profileData = response.data.success ? response.data.data : {
          companyName: seller.seller_name, seller_id: seller.seller_id, quality: seller.quality,
          total_campaigns_served: seller.total_campaigns_served,
          total_buyers_signedup: seller.total_buyers_signedup, member_since: seller.member_since
        };
        setSelectedProfile({ seller_id: seller.seller_id, seller_name: seller.seller_name,
          quality: seller.quality, total_campaigns_served: seller.total_campaigns_served,
          total_buyers_signedup: seller.total_buyers_signedup, member_since: seller.member_since,
          _connection_status: seller._connection_status,
          profile: profileData, hasFullProfile: response.data.has_full_profile || false });
      })
      .catch(function() {
        setSelectedProfile({ seller_id: seller.seller_id, seller_name: seller.seller_name,
          quality: seller.quality, total_campaigns_served: seller.total_campaigns_served,
          total_buyers_signedup: seller.total_buyers_signedup, member_since: seller.member_since,
          _connection_status: seller._connection_status,
          profile: { companyName: seller.seller_name, seller_id: seller.seller_id, quality: seller.quality,
            total_campaigns_served: seller.total_campaigns_served,
            total_buyers_signedup: seller.total_buyers_signedup, member_since: seller.member_since },
          hasFullProfile: false });
      })
      .finally(function() { setLoadingProfile(false); });
  }

  function getConnectionStatus(seller) {
    var sellerId = seller.seller_id || seller;
    // First check backend's _connection_status (from seller's perspective - more accurate)
    if (seller._connection_status === 'connected') return 'connected';
    if (seller._connection_status === 'pending') return 'pending';
    // Fallback to local state (from buyer's perspective)
    if (isConnectedWith(sellerId)) return 'connected';
    if (isPendingWith(sellerId)) return 'pending';
    return 'not_connected';
  }

  function filterSellers() {
    if (!searchText) return sellers;
    var search = searchText.toLowerCase();
    var results = [];
    for (var i = 0; i < sellers.length; i++) {
      var s = sellers[i];
      var name = (s.seller_name || '').toLowerCase();
      var id = (s.seller_id || '').toLowerCase();
      if (name.indexOf(search) >= 0 || id.indexOf(search) >= 0) {
        results.push(s);
      }
    }
    return results;
  }

  function getStats() {
    var connectedCount = 0;
    var pendingCount = 0;
    var topRatedCount = 0;
    for (var j = 0; j < sellers.length; j++) {
      if (isConnectedWith(sellers[j].seller_id)) connectedCount++;
      if (isPendingWith(sellers[j].seller_id)) pendingCount++;
      if (parseFloat(sellers[j].quality) >= 80) topRatedCount++;
    }
    return { connectedCount: connectedCount, pendingCount: pendingCount, topRatedCount: topRatedCount };
  }

  function renderSellerCard(seller, index) {
    var connectionStatus = getConnectionStatus(seller);
    var qualityNum = parseFloat(seller.quality) || 0;
    
    function renderStatusBadge() {
      if (connectionStatus === 'connected') {
        return (
          <Badge className="bg-emerald-100 text-emerald-700 border-0 shadow-sm">
            <CheckCircle className="w-3 h-3 mr-1" />
            Connected
          </Badge>
        );
      } else if (connectionStatus === 'pending') {
        return (
          <Badge className="bg-amber-100 text-amber-700 border-0">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      }
      return null;
    }

    function renderActionButton() {
      if (connectionStatus === 'not_connected') {
        return (
          <Button 
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg shadow-orange-200"
            onClick={function(e) { e.stopPropagation(); openRequestDialog(seller); }}
            data-testid={'send-request-btn-' + index}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Connect
          </Button>
        );
      } else if (connectionStatus === 'pending') {
        return (
          <Button variant="outline" className="w-full border-amber-300 text-amber-600 bg-amber-50" disabled>
            <Clock className="w-4 h-4 mr-2" />
            Request Sent
          </Button>
        );
      } else {
        return (
          <Button variant="outline" className="w-full border-emerald-300 text-emerald-600 bg-emerald-50" disabled>
            <CheckCircle className="w-4 h-4 mr-2" />
            Connected
          </Button>
        );
      }
    }

    return (
      <Card 
        key={seller.seller_id || index} 
        className={`group relative overflow-hidden border-0 shadow-md hover:shadow-xl transition-all duration-300 bg-white ${connectionStatus === 'connected' ? 'ring-2 ring-emerald-400' : connectionStatus === 'pending' ? 'ring-2 ring-amber-400' : ''}`}
        data-testid={'seller-card-' + index}
      >
        {/* Top accent bar - changes color based on status */}
        <div className={`absolute top-0 left-0 right-0 h-1 ${connectionStatus === 'connected' ? 'bg-gradient-to-r from-emerald-400 via-emerald-500 to-green-500' : connectionStatus === 'pending' ? 'bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500' : 'bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500'}`} />
        
        <CardContent className="p-0">
          {/* Header */}
          <div className="p-5 pb-4">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-lg leading-tight">{seller.seller_name || 'Unknown'}</h3>
                  <p className="text-xs text-slate-400 font-mono mt-0.5">{formatSID(seller.seller_id)}</p>
                </div>
              </div>
              {renderStatusBadge()}
            </div>

            {/* Quality Score */}
            <div className="flex items-center justify-between bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-3 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center">
                  <Award className="w-4 h-4 text-orange-500" />
                </div>
                <span className="text-sm font-medium text-slate-600">Quality Score</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={'text-2xl font-bold ' + getQualityColor(seller.quality)}>
                  {seller.quality || '0%'}
                </span>
                {qualityNum >= 80 && (
                  <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">Top Rated</Badge>
                )}
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center p-3 bg-blue-50 rounded-xl">
                <Target className="w-4 h-4 text-blue-500 mx-auto mb-1" />
                <p className="text-lg font-bold text-slate-900">{seller.total_campaigns_served || 0}</p>
                <p className="text-xs text-slate-500">Campaigns</p>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-xl">
                <Users className="w-4 h-4 text-purple-500 mx-auto mb-1" />
                <p className="text-lg font-bold text-slate-900">{seller.total_buyers_signedup || 0}</p>
                <p className="text-xs text-slate-500">Buyers</p>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-xl">
                <Calendar className="w-4 h-4 text-amber-500 mx-auto mb-1" />
                <p className="text-lg font-bold text-slate-900">{formatDateShort(seller.member_since).month} {formatDateShort(seller.member_since).day}</p>
                <p className="text-xs text-slate-500">Since</p>
              </div>
            </div>
          </div>

          {/* Action Footer */}
          <div className="p-4 pt-0 space-y-2">
            {renderActionButton()}
            <Button 
              variant="ghost" 
              className="w-full text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              onClick={function(e) { e.stopPropagation(); openProfileDialog(seller); }}
              data-testid={'view-profile-btn-' + index}
            >
              <FileText className="w-4 h-4 mr-2" />
              View Profile
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  function renderProfileHeader() {
    if (!selectedProfile) return null;
    var profile = selectedProfile.profile;
    var quality = profile.quality || selectedProfile.quality || '0%';
    var qualityNum = parseFloat(quality) || 0;
    var qualityBadgeClass = qualityNum >= 80 
      ? 'bg-emerald-500 text-white' 
      : qualityNum >= 50 
        ? 'bg-amber-500 text-white' 
        : 'bg-slate-500 text-white';
    var hqText = profile.headquarters ? (
      <div className="flex items-center gap-1.5 text-orange-200 text-sm mt-2">
        <MapPin className="w-4 h-4" />
        <span>{profile.headquarters}</span>
      </div>
    ) : null;

    return (
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-orange-500 rounded-full blur-3xl opacity-30" />
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-amber-500 rounded-full blur-3xl opacity-20" />
        </div>
        
        <div className="relative flex items-start gap-5">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-xl shadow-orange-500/20 flex-shrink-0">
            <Building2 className="w-10 h-10 text-white" />
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-white truncate">{profile.companyName || selectedProfile.seller_name}</h2>
                <p className="text-orange-300 font-mono text-sm mt-1">{formatSID(profile.seller_id || selectedProfile.seller_id)}</p>
                {hqText}
              </div>
              
              {/* Quality Score Badge */}
              <div className="flex flex-col items-center">
                <div className={'px-4 py-3 rounded-xl ' + qualityBadgeClass}>
                  <p className="text-3xl font-bold leading-none">{quality}</p>
                </div>
                <p className="text-xs text-slate-400 mt-2">Quality Score</p>
                {qualityNum >= 80 && (
                  <span className="text-xs text-emerald-400 font-medium mt-1 flex items-center gap-1">
                    <Star className="w-3 h-3" /> Top Rated
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderCompanyOverview() {
    if (!selectedProfile) return null;
    var profile = selectedProfile.profile;
    if (!profile.yearEstablished && !profile.companySize && !profile.ownershipType && !profile.website) return null;
    
    var items = [];
    if (profile.yearEstablished) items.push(
      <div key="year" className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 text-center">
        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Established</p>
        <p className="text-xl font-bold text-slate-900">{profile.yearEstablished}</p>
      </div>
    );
    if (profile.companySize) items.push(
      <div key="size" className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 text-center">
        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Company Size</p>
        <p className="text-xl font-bold text-blue-700">{profile.companySize}</p>
        <p className="text-xs text-slate-500">employees</p>
      </div>
    );
    if (profile.ownershipType) items.push(
      <div key="owner" className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-4 text-center">
        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Ownership</p>
        <p className="text-lg font-bold text-purple-700">{profile.ownershipType}</p>
      </div>
    );
    if (profile.website) items.push(
      <div key="web" className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 text-center">
        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Website</p>
        <a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-lg font-bold text-orange-600 hover:text-orange-700 flex items-center justify-center gap-1">
          Visit <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    );
    
    var desc = profile.companyDescription ? (
      <div className="mt-5 p-4 bg-slate-50 rounded-xl border border-slate-100">
        <p className="text-sm text-slate-600 leading-relaxed">{profile.companyDescription}</p>
      </div>
    ) : null;
    
    return (
      <ProfileSection title="Company Overview" icon={Building2} iconColor="text-orange-500" iconBg="bg-orange-100">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{items}</div>
        {desc}
      </ProfileSection>
    );
  }

  function renderPerformanceMetrics() {
    if (!selectedProfile) return null;
    var profile = selectedProfile.profile;
    
    var extraMetrics = null;
    if (profile.monthlyVolume || profile.dataFreshness || profile.turnaroundTime) {
      var metricItems = [];
      if (profile.monthlyVolume) metricItems.push(
        <div key="vol" className="text-center p-4 bg-gradient-to-br from-indigo-50 to-violet-50 rounded-xl border border-indigo-100">
          <p className="text-2xl font-bold text-indigo-700">{profile.monthlyVolume}</p>
          <p className="text-xs text-indigo-600 font-medium">Monthly Volume</p>
        </div>
      );
      if (profile.dataFreshness) metricItems.push(
        <div key="fresh" className="text-center p-4 bg-gradient-to-br from-cyan-50 to-teal-50 rounded-xl border border-cyan-100">
          <p className="text-2xl font-bold text-cyan-700">{profile.dataFreshness} <span className="text-lg">days</span></p>
          <p className="text-xs text-cyan-600 font-medium">Data Freshness</p>
        </div>
      );
      if (profile.turnaroundTime) metricItems.push(
        <div key="turn" className="text-center p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-100">
          <p className="text-2xl font-bold text-amber-700">{profile.turnaroundTime}</p>
          <p className="text-xs text-amber-600 font-medium">Turnaround</p>
        </div>
      );
      extraMetrics = <div className="grid grid-cols-3 gap-3 mt-5">{metricItems}</div>;
    }
    
    return (
      <ProfileSection title="Performance Metrics" icon={BarChart3} iconColor="text-blue-500" iconBg="bg-blue-100">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="relative overflow-hidden text-center p-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg shadow-blue-200">
            <div className="absolute -top-4 -right-4 w-16 h-16 bg-white/10 rounded-full" />
            <Target className="w-6 h-6 text-blue-100 mx-auto mb-2" />
            <p className="text-3xl font-bold text-white">{profile.total_campaigns_served || selectedProfile.total_campaigns_served || 0}</p>
            <p className="text-xs text-blue-100 font-medium">Campaigns Served</p>
          </div>
          <div className="relative overflow-hidden text-center p-4 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg shadow-purple-200">
            <div className="absolute -top-4 -right-4 w-16 h-16 bg-white/10 rounded-full" />
            <Users className="w-6 h-6 text-purple-100 mx-auto mb-2" />
            <p className="text-3xl font-bold text-white">{profile.total_buyers_signedup || selectedProfile.total_buyers_signedup || 0}</p>
            <p className="text-xs text-purple-100 font-medium">Active Buyers</p>
          </div>
          <div className="relative overflow-hidden text-center p-4 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg shadow-emerald-200">
            <div className="absolute -top-4 -right-4 w-16 h-16 bg-white/10 rounded-full" />
            <TrendingUp className="w-6 h-6 text-emerald-100 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{profile.avgLeadToClose || 'N/A'}</p>
            <p className="text-xs text-emerald-100 font-medium">Lead-to-Close</p>
          </div>
          <div className="relative overflow-hidden text-center p-4 bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl shadow-lg shadow-slate-300">
            <div className="absolute -top-4 -right-4 w-16 h-16 bg-white/10 rounded-full" />
            <Calendar className="w-6 h-6 text-slate-200 mx-auto mb-2" />
            <p className="text-sm font-bold text-white">{formatDate(profile.member_since || selectedProfile.member_since)}</p>
            <p className="text-xs text-slate-300 font-medium">Member Since</p>
          </div>
        </div>
        {extraMetrics}
      </ProfileSection>
    );
  }

  function renderGeographicCoverage() {
    if (!selectedProfile) return null;
    var tags = selectedProfile.profile.tags || {};
    if (!tags.locations && !tags.geographies && !tags.languages) return null;
    
    var sections = [];
    if (tags.locations && tags.locations.length > 0) sections.push(
      <div key="loc" className="bg-gradient-to-br from-indigo-50 to-violet-50 rounded-xl p-4 border border-indigo-100">
        <p className="text-xs text-indigo-600 uppercase tracking-wide font-semibold mb-3 flex items-center gap-1">
          <MapPin className="w-3 h-3" /> Locations
        </p>
        <BadgeList items={tags.locations} colorClass="bg-white text-indigo-700 border border-indigo-200 shadow-sm" />
      </div>
    );
    if (tags.geographies && tags.geographies.length > 0) sections.push(
      <div key="geo" className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-100">
        <p className="text-xs text-blue-600 uppercase tracking-wide font-semibold mb-3 flex items-center gap-1">
          <Globe className="w-3 h-3" /> Geographies
        </p>
        <BadgeList items={tags.geographies} colorClass="bg-white text-blue-700 border border-blue-200 shadow-sm" />
      </div>
    );
    if (tags.languages && tags.languages.length > 0) sections.push(
      <div key="lang" className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
        <p className="text-xs text-purple-600 uppercase tracking-wide font-semibold mb-3">Languages</p>
        <BadgeList items={tags.languages} colorClass="bg-white text-purple-700 border border-purple-200 shadow-sm" />
      </div>
    );
    
    return (
      <ProfileSection title="Geographic Coverage" icon={Globe} iconColor="text-indigo-500" iconBg="bg-indigo-100">
        <div className="space-y-4">{sections}</div>
      </ProfileSection>
    );
  }

  function renderCapabilities() {
    if (!selectedProfile) return null;
    var profile = selectedProfile.profile;
    if (!profile.leadTypes && !profile.channels && !profile.industries) return null;
    
    var sections = [];
    if (profile.leadTypes && profile.leadTypes.length > 0) sections.push(
      <div key="lead" className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-100">
        <p className="text-xs text-emerald-600 uppercase tracking-wide font-semibold mb-3 flex items-center gap-1">
          <Target className="w-3 h-3" /> Lead Types
        </p>
        <BadgeList items={profile.leadTypes} colorClass="bg-white text-emerald-700 border border-emerald-200 shadow-sm" />
      </div>
    );
    if (profile.channels && profile.channels.length > 0) sections.push(
      <div key="chan" className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-4 border border-teal-100">
        <p className="text-xs text-teal-600 uppercase tracking-wide font-semibold mb-3 flex items-center gap-1">
          <Zap className="w-3 h-3" /> Channels
        </p>
        <BadgeList items={profile.channels} colorClass="bg-white text-teal-700 border border-teal-200 shadow-sm" />
      </div>
    );
    if (profile.industries && profile.industries.length > 0) sections.push(
      <div key="ind" className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100">
        <p className="text-xs text-amber-600 uppercase tracking-wide font-semibold mb-3 flex items-center gap-1">
          <Building2 className="w-3 h-3" /> Industries
        </p>
        <BadgeList items={profile.industries} colorClass="bg-white text-amber-700 border border-amber-200 shadow-sm" />
      </div>
    );
    
    return (
      <ProfileSection title="Lead Generation Capabilities" icon={Zap} iconColor="text-amber-500" iconBg="bg-amber-100">
        <div className="space-y-4">{sections}</div>
      </ProfileSection>
    );
  }

  function renderValidation() {
    if (!selectedProfile) return null;
    var profile = selectedProfile.profile;
    if (!profile.validation || profile.validation.length === 0) return null;
    
    var badges = [];
    for (var i = 0; i < profile.validation.length; i++) {
      badges.push(
        <Badge key={i} className="bg-white text-emerald-700 border border-emerald-200 shadow-sm px-3 py-1.5 flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          {profile.validation[i]}
        </Badge>
      );
    }
    var deliveryInfo = profile.deliveryFormat ? (
      <div className="mt-5 p-4 bg-slate-50 rounded-xl border border-slate-100">
        <p className="text-sm">
          <span className="text-slate-500">Delivery Format:</span>{' '}
          <span className="font-semibold text-slate-900">{profile.deliveryFormat}</span>
        </p>
      </div>
    ) : null;
    
    return (
      <ProfileSection title="Validation & Quality" icon={ShieldCheck} iconColor="text-emerald-500" iconBg="bg-emerald-100">
        <div className="flex flex-wrap gap-2">{badges}</div>
        {deliveryInfo}
      </ProfileSection>
    );
  }

  function renderCertifications() {
    if (!selectedProfile) return null;
    var tags = selectedProfile.profile.tags || {};
    if (!tags.iso || tags.iso.length === 0) return null;
    
    var badges = [];
    for (var i = 0; i < tags.iso.length; i++) {
      badges.push(
        <div key={i} className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-4 text-center">
          <Award className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="font-semibold text-amber-800">{tags.iso[i]}</p>
        </div>
      );
    }
    
    return (
      <ProfileSection title="Certifications" icon={Award} iconColor="text-amber-500" iconBg="bg-amber-100">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{badges}</div>
      </ProfileSection>
    );
  }

  function renderCompliance() {
    if (!selectedProfile) return null;
    var profile = selectedProfile.profile;
    if (!profile.gdpr && !profile.ccpa && !profile.canspam && !profile.doubleOptin) return null;
    
    var extraInfo = null;
    if (profile.consentMethod || profile.suppressionHandling || profile.privacyPolicy) {
      var infoItems = [];
      if (profile.consentMethod) infoItems.push(
        <div key="consent" className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Consent Method</p>
          <p className="font-medium text-slate-900">{profile.consentMethod}</p>
        </div>
      );
      if (profile.suppressionHandling) infoItems.push(
        <div key="supp" className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Suppression</p>
          <p className="font-medium text-slate-900">{profile.suppressionHandling}</p>
        </div>
      );
      if (profile.privacyPolicy) infoItems.push(
        <div key="priv" className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Privacy Policy</p>
          <a href={profile.privacyPolicy} target="_blank" rel="noopener noreferrer" className="font-medium text-orange-600 hover:text-orange-700 flex items-center gap-1">
            View Policy <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      );
      extraInfo = <div className="grid grid-cols-3 gap-3 mt-5">{infoItems}</div>;
    }
    
    return (
      <ProfileSection title="Compliance" icon={ShieldCheck} iconColor="text-blue-500" iconBg="bg-blue-100">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ComplianceBadge label="GDPR" isCompliant={profile.gdpr === 'yes'} />
          <ComplianceBadge label="CCPA" isCompliant={profile.ccpa === 'yes'} />
          <ComplianceBadge label="CAN-SPAM" isCompliant={profile.canspam === 'yes'} />
          <ComplianceBadge label="Double Opt-in" isCompliant={profile.doubleOptin === 'yes'} />
        </div>
        {extraInfo}
      </ProfileSection>
    );
  }

  function renderContactNotice() {
    if (!selectedProfile) return null;
    var connectionStatus = getConnectionStatus(selectedProfile.seller_id);
    if (connectionStatus === 'connected') return null;
    
    var actionElement = null;
    if (connectionStatus === 'not_connected') {
      actionElement = (
        <Button 
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-lg shadow-orange-200" 
          onClick={function() { setProfileDialogOpen(false); openRequestDialog(selectedProfile); }}
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Send Connection Request
        </Button>
      );
    } else {
      actionElement = (
        <Badge className="bg-amber-100 text-amber-700 px-4 py-2 text-sm">
          <Clock className="w-4 h-4 mr-2" />
          Connection Request Pending
        </Badge>
      );
    }
    
    return (
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500 rounded-full blur-3xl opacity-20" />
        </div>
        <div className="relative flex items-start gap-5">
          <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <Lock className="w-7 h-7 text-slate-300" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-white text-lg mb-2">Unlock Contact Information</h4>
            <p className="text-slate-400 text-sm mb-4">Connect with this seller to access contact details, account manager information, and start collaborating.</p>
            {actionElement}
          </div>
        </div>
      </div>
    );
  }

  function renderProfileContent() {
    if (loadingProfile) {
      return <div className="flex items-center justify-center py-20"><div className="text-center"><Loader2 className="w-10 h-10 animate-spin text-orange-500 mx-auto mb-3" /><p className="text-slate-500">Loading seller profile...</p></div></div>;
    }
    if (!selectedProfile) {
      return <div className="text-center py-20 text-slate-500">Failed to load profile data</div>;
    }
    return (
      <ScrollArea className="h-[65vh]">
        <div className="space-y-6 p-1">
          {renderProfileHeader()}
          {renderCompanyOverview()}
          {renderPerformanceMetrics()}
          {renderGeographicCoverage()}
          {renderCapabilities()}
          {renderValidation()}
          {renderCertifications()}
          {renderCompliance()}
          {renderContactNotice()}
        </div>
      </ScrollArea>
    );
  }

  function renderHeroHeader() {
    return (
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-8 mb-8">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-40 h-40 bg-orange-500 rounded-full blur-3xl opacity-20" />
          <div className="absolute bottom-0 right-0 w-60 h-60 bg-amber-500 rounded-full blur-3xl opacity-10" />
        </div>
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-3 py-1 text-xs font-medium bg-orange-500/20 text-orange-400 rounded-full border border-orange-500/30">
              Lead Provider Network
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Browse Sellers
          </h1>
          <p className="text-slate-400 max-w-xl">
            Discover and connect with verified lead generation providers. View profiles, compare quality scores, and build your partner network.
          </p>
        </div>
        <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden lg:block">
          <div className="w-32 h-32 rounded-full bg-gradient-to-br from-orange-500/20 to-amber-500/10 blur-2xl" />
        </div>
      </div>
    );
  }

  function renderStatsBar() {
    if (loading || error || sellers.length === 0) return null;
    var stats = getStats();
    
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{sellers.length}</p>
              <p className="text-xs text-slate-500">Total Sellers</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">{stats.connectedCount}</p>
              <p className="text-xs text-slate-500">Connected</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.pendingCount}</p>
              <p className="text-xs text-slate-500">Pending</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Star className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.topRatedCount}</p>
              <p className="text-xs text-slate-500">Top Rated</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderSkeletons() {
    var items = [];
    for (var i = 0; i < 6; i++) {
      items.push(
        <Card key={i} className="border-0 shadow-md">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-12 w-12 rounded-xl" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
            <Skeleton className="h-16 w-full rounded-xl" />
            <div className="grid grid-cols-3 gap-2">
              <Skeleton className="h-16 rounded-xl" />
              <Skeleton className="h-16 rounded-xl" />
              <Skeleton className="h-16 rounded-xl" />
            </div>
            <Skeleton className="h-10 w-full rounded-lg" />
          </CardContent>
        </Card>
      );
    }
    return items;
  }

  var filteredSellers = filterSellers();

  var sellerCards = [];
  if (loading) {
    sellerCards = renderSkeletons();
  } else if (filteredSellers.length === 0) {
    sellerCards.push(
      <div key="empty" className="col-span-full text-center py-16">
        <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <Search className="w-10 h-10 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800 mb-2">No Sellers Found</h3>
        <p className="text-slate-500">Try adjusting your search criteria</p>
      </div>
    );
  } else {
    for (var m = 0; m < filteredSellers.length; m++) {
      sellerCards.push(renderSellerCard(filteredSellers[m], m));
    }
  }

  var profileFooterButton = null;
  if (selectedProfile && !isConnectedWith(selectedProfile.seller_id) && !isPendingWith(selectedProfile.seller_id)) {
    profileFooterButton = <Button className="bg-orange-500 hover:bg-orange-600" onClick={function() { setProfileDialogOpen(false); openRequestDialog(selectedProfile); }}><UserPlus className="w-4 h-4 mr-2" />Send Connection Request</Button>;
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 py-8" data-testid="sellers-marketplace-page">
          
          {renderHeroHeader()}
          {renderStatsBar()}

          {/* Filter Section */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 mb-6">
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <div className="flex-1 w-full">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    placeholder="Search by seller name or SID..." 
                    className="pl-10 border-slate-200 focus:border-orange-300 focus:ring-orange-200" 
                    value={searchText} 
                    onChange={function(e) { setSearchText(e.target.value); }}
                    data-testid="seller-search-input"
                  />
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={fetchSellers} 
                disabled={loading}
                className="border-slate-200"
              >
                <RefreshCw className={'w-4 h-4 mr-2 ' + (loading ? 'animate-spin' : '')} />
                Refresh
              </Button>
            </div>
            
            {/* Results count */}
            {!loading && !error && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-sm text-slate-500">
                  Showing {filteredSellers.length} of {sellers.length} sellers
                  {searchText && ' (filtered)'}
                </p>
              </div>
            )}
          </div>

          {/* Error State */}
          {error && (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Connection Error</h3>
              <p className="text-slate-500 mb-6">{error}</p>
              <Button onClick={fetchSellers} className="bg-orange-500 hover:bg-orange-600">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          )}

          {/* Seller Cards Grid */}
          {!error && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sellerCards}
            </div>
          )}
        </div>
      </div>

      {/* Connection Request Dialog */}
      <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-orange-500" />
              Send Connection Request
            </DialogTitle>
            <DialogDescription>
              Send a connection request to <strong>{selectedSeller ? selectedSeller.seller_name : 'this seller'}</strong> to start collaborating.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input id="subject" value={requestSubject} onChange={function(e) { setRequestSubject(e.target.value); }} data-testid="request-subject-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea id="message" value={requestMessage} onChange={function(e) { setRequestMessage(e.target.value); }} rows={4} data-testid="request-message-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeRequestDialog} disabled={sendingRequest}>Cancel</Button>
            <Button onClick={handleSendRequest} disabled={sendingRequest} className="bg-orange-500 hover:bg-orange-600" data-testid="confirm-send-request-btn">
              {sendingRequest ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send Request</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Seller Profile Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-orange-500" />
              Seller Profile
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6">{renderProfileContent()}</div>
          <div className="border-t bg-slate-50 px-6 py-4 flex justify-end gap-3">
            <Button variant="outline" onClick={function() { setProfileDialogOpen(false); }}>Close</Button>
            {profileFooterButton}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default SellersMarketplacePage;
